import React, { useEffect, useRef } from "react";
import type * as T from "@src/types";
import * as portfolioUtils from "@src/utils/portfolioData";
import {
  BaselineSeries,
  ColorType,
  CrosshairMode,
  LineSeries,
  LineStyle,
  createChart,
} from "lightweight-charts";

interface Props {
  strategies: T.PortfolioTypes.StrategyPerformance[];
  portfolioSnapshots: T.PortfolioTypes.PortfolioDailySnapshot[];
  showAccumulated: boolean;
  showIndividualStrategies: boolean;
  height?: number;
}

export const PortfolioChart: React.FC<Props> = ({
  strategies,
  portfolioSnapshots,
  showAccumulated,
  showIndividualStrategies,
  height = 400,
}) => {
  const chartRef = useRef<T.TVL.IChartApi | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    // Clear previous chart
    if (chartRef.current) {
      chartRef.current.remove();
      chartRef.current = null;
    }

    // Get container width
    const containerWidth = containerRef.current.clientWidth || 800;

    // Create chart with dark theme
    const chart = createChart(containerRef.current, {
      width: containerWidth,
      height: height,
      layout: {
        background: { type: ColorType.Solid, color: "#0f0f0f" },
        textColor: "#d1d4dc",
      },
      rightPriceScale: {
        visible: true,
        borderColor: "#2a2e39",
        scaleMargins: {
          top: 0.1,
          bottom: 0.1,
        },
      },
      leftPriceScale: {
        visible: showAccumulated,
        borderColor: "#2a2e39",
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: "#758696",
          width: 1,
          style: LineStyle.Dashed,
        },
        horzLine: {
          color: "#758696",
          width: 1,
          style: LineStyle.Dashed,
        },
      },
      grid: {
        vertLines: { color: "rgba(42, 46, 57, 0.6)" },
        horzLines: { color: "rgba(42, 46, 57, 0.6)" },
      },
      timeScale: {
        borderColor: "#2a2e39",
        timeVisible: true,
        secondsVisible: false,
      },
    });

    chartRef.current = chart;

    // Add zero line for reference
    const zeroLineSeries = chart.addSeries(LineSeries, {
      color: "rgba(255, 255, 255, 0.3)",
      lineWidth: 1,
      lineStyle: LineStyle.Dashed,
      title: "Break Even",
      crosshairMarkerVisible: false,
    });

    // Get all timestamps from portfolio snapshots
    if (portfolioSnapshots.length > 0) {
      const zeroLineData = portfolioSnapshots.map((snapshot) => ({
        time: snapshot.timestamp as T.TVL.Time,
        value: 0,
      }));
      zeroLineSeries.setData(zeroLineData);
    }

    // Add accumulated portfolio PnL series (on right scale)
    if (showAccumulated && portfolioSnapshots.length > 0) {
      const portfolioPnLSeries = chart.addSeries(BaselineSeries, {
        title: "Total PnL",
        baseValue: { type: "price", price: 0 },
        topLineColor: "rgba(76, 175, 80, 0.8)",
        topFillColor1: "rgba(76, 175, 80, 0.3)",
        topFillColor2: "rgba(76, 175, 80, 0.05)",
        bottomLineColor: "rgba(244, 67, 54, 0.8)",
        bottomFillColor1: "rgba(244, 67, 54, 0.05)",
        bottomFillColor2: "rgba(244, 67, 54, 0.3)",
        lineWidth: 3,
        priceScaleId: "right",
      });

      const portfolioData = portfolioSnapshots.map((snapshot) => ({
        time: snapshot.timestamp as T.TVL.Time,
        value: snapshot.totalPnl,
      }));
      portfolioPnLSeries.setData(portfolioData);
    }

    // Add individual strategy PnL series
    if (showIndividualStrategies && strategies.length > 0) {
      const colors = portfolioUtils.getStrategyColors(strategies.length);

      strategies.forEach((strategy, index) => {
        if (strategy.dailySnapshots.length === 0) return;

        const strategySeries = chart.addSeries(LineSeries, {
          title:
            strategy.executionName.length > 20
              ? strategy.executionName.substring(0, 20) + "..."
              : strategy.executionName,
          color: colors[index],
          lineWidth: 2,
          crosshairMarkerVisible: true,
          crosshairMarkerRadius: 4,
        });

        const strategyData = strategy.dailySnapshots.map((snapshot) => ({
          time: snapshot.timestamp as T.TVL.Time,
          value: snapshot.pnl,
        }));

        strategySeries.setData(strategyData);
      });
    }

    // Fit content to show all data
    chart.timeScale().fitContent();

    // Handle resize
    const handleResize = () => {
      if (containerRef.current && chartRef.current) {
        chartRef.current.applyOptions({
          width: containerRef.current.clientWidth,
        });
      }
    };

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
      if (chartRef.current) {
        chartRef.current.remove();
        chartRef.current = null;
      }
    };
  }, [strategies, portfolioSnapshots, showAccumulated, showIndividualStrategies, height]);

  return <div ref={containerRef} style={{ width: "100%", height: `${height}px` }} />;
};
